import appConfig from '../../config/appConfig';
import * as errorPageTypes from '../../consts/app/errorPageTypes';
import * as iccMethods from '../../consts/icc/iccMethods';
import {GET_TOKEN} from '../../consts/icc/iccMethods';
import {isInvalidGrant} from '../../consts/icc/iccResponseErrorTypes';
import * as logMessageTypes from '../../consts/log/logMessageTypes';
import * as uamTokenIdTypes from '../../consts/uam/uamTokenIdTypes';
import httpStatusTypesService from '../../services/server/httpStatusTypesService';
import {setYapAssets} from '../../state/ducks/yapEncrypted';
import {selectHolderUamAuthToken, selectUamAuthToken} from '../../state/selectors/auth';
import {makeSelectIotDeviceMergedWithConsumerProduct} from '../../state/selectors/consumer';
import {selectIsHolderConnected} from '../../state/selectors/iotDevice';
import {dispatch, getState} from '../../state/store';
import stringUtils from '../../utils/stringUtils';
import urlUtils from '../../utils/urlUtils';
import backendService from '../app/backendService';
import authDataService from '../auth/authDataService';
import authDataStoreService from '../auth/authDataStoreService';
import * as gigyaService from '../gigya/gigyaService';
import HttpErrorHandlerService from '../httpErrorHandlerService';
import {getMappedInitialLanguageCode} from '../localization/localizationService';
import log from '../logger/log';
import {authMapping, authUamMapping, loginTokenMapping} from '../mapping/iccMappings';
import marketTypeService from '../marketTypeService';
import pairingService from '../pairingService';
import productHelpers from '../product/productHelpers';
import appRouterService from '../route/appRouterService';
import server from '../server/server';
import storageService from '../storage/storageService';
import iccConsumerProducts from './iccConsumerProducts';
import iccResponseErrorCheckService from './iccResponseErrorCheckService';

let isTokenRefreshInProgress = false;
const getIccApiUrl = (methodName) => urlUtils.join(appConfig.getIccApiUrl(), methodName);

const getHeaders = () => {
    return {
        a: appConfig.getIccApiHeader_a(),
        Accept: 'application/json',
        Authorization: appConfig.getIccApiHeaderAuthorization(),
        'Content-Type': 'application/x-www-form-urlencoded',
        l: getMappedInitialLanguageCode()?.toUpperCase(),
        m: appConfig.getIccApiHeader_m(),
        v: appConfig.getAppVersion(),
    };
};

const getBody = (isAnonymous) => {
    const authUsername = authDataStoreService.getAuthUsername();

    return {
        grant_type: 'password',
        password: '',
        scope: appConfig.getIccApiBodyScope(),
        username: isAnonymous ? 'anonymous' : authUsername ? authUsername : appConfig.getIccApiHeaderUsername(),
    };
};

const onIccApiMethodResponse = (response, isAnonymous) => {
    const responseData = authMapping(response.data);

    authDataStoreService.setIccTokenData({...responseData, isAnonymous, isRequestFailed: false});

    return responseData;
};

const refreshIccTokenIfAllowed = (isAnonymousUserAllowed) => {
    if (!isTokenRefreshInProgress) {
        isTokenRefreshInProgress = true;

        return refreshIccToken(isAnonymousUserAllowed);
    }
};

const getSpiceToken = async () => {
    const spiceTokenFromState = authDataStoreService.getSpiceToken();

    if (spiceTokenFromState) return spiceTokenFromState;

    const {spiceToken} = await authDataService.getInitialCredentials();

    return spiceToken;
};

const getAuthData = async (isAnonymous) => {
    const isGamBackend = backendService.isGamBackend();
    const isDCE2 = marketTypeService.isDCE2();
    const anonymousToken = appConfig.getAnonymousToken();

    if (isGamBackend && isDCE2) return {isAnonymous: true, password: anonymousToken};

    const spiceToken = isAnonymous ? anonymousToken : await getSpiceToken();
    const gigyaToken = gigyaService.getGigyaToken();
    const token = isDCE2 ? gigyaToken : spiceToken;
    const isTokenValid = !stringUtils.isNullOrEmpty(token);

    if (isTokenValid) return {isAnonymous, password: token};

    return {isAnonymous: true, password: anonymousToken};
};

const isRefreshGigyaToken = async (e, isRetry) => {
    const isInvalid = isInvalidGrant(e);
    const isDCE2 = marketTypeService.isDCE2();

    if (isInvalid && isDCE2 && !isRetry) {
        return await gigyaService.refreshGigyaToken();
    }

    return false;
};

const fetchIccToken = async (props) => {
    const {isAnonymous, isStandaloneLoginPage, isRetry} = props || {};
    const isAnonymousUserAllowed = !!isAnonymous;
    const {password, isAnonymous: isAnonymousToken} = await getAuthData(isAnonymousUserAllowed);

    if (!password || isAnonymousUserAllowed !== isAnonymousToken) {
        throw new Error('fetchIccToken Error. No token or anonymous token is not allowed here');
    }

    const data = urlUtils.stringify({...getBody(isAnonymousToken), password});

    try {
        const response = await getToken(data, getHeaders());

        onIccApiMethodResponse(response, isAnonymousToken);

        log.info(`Request has successful response. Url: ${GET_TOKEN} isAnonymous: ${!!isAnonymousToken}`);
    } catch (e) {
        const errorStatus = e?.response?.status;
        const isTokenNotValid = httpStatusTypesService.isBadRequest(errorStatus);
        const isGigyaTokenRefreshed = await isRefreshGigyaToken(e, isRetry);

        if (isGigyaTokenRefreshed) {
            return fetchIccToken({...props, isRetry: true});
        } else if (isStandaloneLoginPage && isTokenNotValid) {
            throw e;
        } else {
            new HttpErrorHandlerService().handleError(e);
            authDataStoreService.setIccTokenData({isRequestFailed: true});
        }
    }
};

const refreshIccToken = async ({isAnonymousUserAllowed, isRetry} = {}) => {
    const {password} = await getAuthData(isAnonymousUserAllowed);
    const refreshToken = authDataStoreService.getRefreshToken();

    if (password && refreshToken) {
        const data = urlUtils.stringify({
            ...getBody(),
            password,
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
        });

        try {
            const response = await getToken(data, getHeaders());

            const responseMapped = onIccApiMethodResponse(response);

            log.debug(
                `iccService: refreshIccToken, request: "${iccMethods.GET_TOKEN}" has successful response`,
                logMessageTypes.ICC
            );

            isTokenRefreshInProgress = false;
            return responseMapped;
        } catch (e) {
            isTokenRefreshInProgress = false;

            const errorStatus = e?.response?.status;
            const isRememberMeEnabled = appConfig.getIsRememberMeEnabled();

            if (isRememberMeEnabled) {
                storageService.removeUserSessionDataFromStorage();
                appRouterService.forwardToSelectMarketPage();
            }

            log.info(
                `iccService: refreshIccToken, request: "${iccMethods.GET_TOKEN}", error: ${e}`,
                logMessageTypes.ICC
            );

            if (errorStatus) {
                const isGigyaTokenRefreshed = await isRefreshGigyaToken(e, isRetry);

                if (isGigyaTokenRefreshed) {
                    return refreshIccToken({isAnonymousUserAllowed, isRetry: true});
                } else {
                    log.info(`iccService: refreshIccToken, BadRequest. Redirect to Global Error page`, {
                        logMessageType: logMessageTypes.ICC,
                    });
                    appRouterService.forwardToIccErrorHandlingPage(errorStatus);
                    return;
                }
            }

            throw e;
        }
    } else {
        isTokenRefreshInProgress = false;
        log.error(
            `iccService: refreshIccToken, required auth data is empty, token: ${password}, refreshToken: ${refreshToken}`,
            logMessageTypes.ICC
        );
        appRouterService.forwardToErrorHandlingPage(errorPageTypes.ICC_TEMPORARY_ERROR);
    }
};

const getDeviceData = (isHolder) => {
    const iotDevice = makeSelectIotDeviceMergedWithConsumerProduct()(getState());
    const {deviceSerialNumber, codentifyOriginal} = (isHolder ? iotDevice.holder : iotDevice.device) || {};

    return {deviceSerialNumber, codentifyOriginal};
};

const fetchUamToken = async ({isRetry, isHolder, codentify} = {}) => {
    const getCondentifyID = () => codentify || getDeviceData(isHolder)?.codentifyOriginal;
    let codentifyID = getCondentifyID();
    const isDeviceCodentifyValid = (codentifyID) => productHelpers.isOriginalCodentifyValid(codentifyID);

    if (!isDeviceCodentifyValid(codentifyID)) {
        const {deviceSerialNumber} = getDeviceData(isHolder);

        const isValid = await iccConsumerProducts.getDetailedProduct(deviceSerialNumber);

        if (!isValid) {
            log.info(
                `iccService: fetchUamToken, codentify is not defined. isHolder: ${!!isHolder}`,
                logMessageTypes.ICC
            );
            return false;
        }

        codentifyID = getCondentifyID();
    }

    if (!isDeviceCodentifyValid(codentifyID)) {
        const state = getState();
        const idToken = isHolder ? selectHolderUamAuthToken(state) : selectUamAuthToken(state);

        if (idToken) return false;

        log.info(`iccService: fetchUamToken, token is not defined. isHolder: ${!!isHolder}`, {
            logMessageType: logMessageTypes.ICC,
        });

        if (isHolder) {
            pairingService.disconnectAndClearHolderState();
        } else if (backendService.isUamBackend()) {
            appRouterService.forwardToErrorHandlingPage(errorPageTypes.ICC_TEMPORARY_ERROR);
        }
        return false;
    }

    const appVersion = appConfig.getAppVersion();
    const iccMethod = iccMethods.GET_UAM_TOKEN_V5;
    const message = `iccService: fetchUamToken, request: "${iccMethod}" for ${codentifyID} isHolder: ${isHolder}`;

    try {
        const authToken = await authDataService.getAuthToken();

        if (!authToken) return false;

        const requestConfig = {
            headers: {Authorization: 'Bearer ' + authToken, v: appVersion, et: gigyaService.getGigyaToken()},
            data: {
                assets: [
                    {
                        id: codentifyID,
                        idtype: uamTokenIdTypes.TRACKING,
                    },
                ],
            },
        };
        const response = await server.post(getIccApiUrl(iccMethod), {...requestConfig});
        const responseData = authUamMapping(response.data, isHolder);

        if (codentify) {
            //only for usage
            authDataStoreService.setAssetIdApiList({...responseData, codentify});
        } else {
            dispatch(setYapAssets(responseData.assets));
            authDataStoreService.setUamApiData({...responseData, isHolder});
        }

        log.debug(`${message} has successful response`, {logMessageType: logMessageTypes.ICC});
        return response.data;
    } catch (e) {
        log.error(`${message}, error: ${e}`, {logMessageType: logMessageTypes.ICC});
        if (isRetry) return false;

        await iccResponseErrorCheckService(e);
        return await fetchUamToken({isRetry: true, isHolder, codentify});
    }
};

const fetchUamTokensIgnoreErrors = async () => {
    const promises = [fetchUamToken()];

    const isHolderConnected = selectIsHolderConnected(getState());

    if (isHolderConnected) {
        promises.push(fetchUamToken({isHolder: true}));
    }

    await Promise.all(promises).catch(() => {
        //
    });
};

const fetchSSOLink = async (deeplinkId) => {
    const appVersion = appConfig.getAppVersion();
    const authToken = await authDataService.getAuthToken();
    const headers = {Authorization: 'Bearer ' + authToken, v: appVersion};
    const login = authDataStoreService.getAuthUsername();
    const password = authDataStoreService.getSpiceToken();

    if (login) {
        const data = {
            Login: login,
            Password: password,
            AutoLoginLinkName: deeplinkId,
        };

        try {
            const response = await getLoginToken(data, headers);

            const {campaignUrlPersonal} = loginTokenMapping(response.data);

            log.debug(
                `iccService: fetchSSOLink, request: "${iccMethods.GET_CONSUMER_LOGIN_TOKEN}" has successful response`,
                logMessageTypes.ICC
            );

            return campaignUrlPersonal;
        } catch (e) {
            const errorStatus = e?.response?.status;

            log.error(
                `iccService: fetchSSOLink, request: "${iccMethods.GET_CONSUMER_LOGIN_TOKEN}", error: ${e}`,
                logMessageTypes.ICC
            );
            new HttpErrorHandlerService().handleError(e);

            return {errorStatus};
        }
    } else {
        log.debug(`iccService: fetchSSOLink is canceled, login is empty`, {logMessageType: logMessageTypes.ICC});
    }
};

const getToken = (data, headers) => server.post(getIccApiUrl(iccMethods.GET_TOKEN), {data, headers});

const getLoginToken = (data, headers) =>
    server.post(getIccApiUrl(iccMethods.GET_CONSUMER_LOGIN_TOKEN), {data, headers});

export default {
    refreshIccTokenIfAllowed,
    fetchIccToken,
    fetchSSOLink,
    fetchUamToken,
    fetchUamTokensIgnoreErrors,
    refreshIccToken,
};
